import React, { useCallback, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import type { InputActionMeta, MultiValueGenericProps, OnChangeValue, OptionProps, StylesConfig } from "react-select";
import AsyncSelect from "react-select/async";

import { faBullseyeArrow } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { NodesFromEdges } from "~/lib/utils";
import { environment } from "~/models";

import type {
	SelectKeyResultDropdownQuery,
	SelectKeyResultDropdownQuery$data,
} from "~/__generated__/SelectKeyResultDropdownQuery.graphql";

import styles from "~/lib/prose/plugins/objectives/SelectKeyResultDropdown.module.css";

export type KeyResultType = NodesFromEdges<SelectKeyResultDropdownQuery$data["keyResults"]>;
export type KeyResultsEdgesType = readonly ({ node: KeyResultType } | null)[];

export interface GroupedKeyResultsOption {
	readonly label: string;
	readonly options: readonly KeyResultType[];
}

const keyResultsQuery = graphql`
	query SelectKeyResultDropdownQuery($titleIncludingObjectives: String, $assignedTo: [ID!], $cycles: [ID!]) {
		keyResults(
			titleIncludingObjectives: $titleIncludingObjectives
			assignedTo: $assignedTo
			cycles: $cycles
			first: 25
		) {
			edges {
				node {
					id
					title
					objective {
						title
					}
				}
			}
		}
	}
`;

interface SelectKeyResultProps {
	values: KeyResultType[];
	onChange(selectedKeyResults: KeyResultType[]): void;
	assignedTo: string[];
	cycles: string[];
}

const formatGroupLabel = (data: GroupedKeyResultsOption) => {
	return (
		<div>
			<FontAwesomeIcon icon={faBullseyeArrow} />
			{data.label.trim() ? (
				<span className={styles.groupLabelText}>{data.label.trim()}</span>
			) : (
				<span className={styles.groupLabelText}>
					<i>Untitled objective</i>
				</span>
			)}
		</div>
	);
};

const formatOkrsOptions = (keyResultsEdges: KeyResultsEdgesType) => {
	const keyResultsGroups: GroupedKeyResultsOption[] = [];
	const dict = new Map<string, KeyResultType[]>();

	keyResultsEdges.forEach(edge => {
		const keyResult = edge?.node;
		if (!keyResult) {
			return;
		}
		if (dict.has(keyResult.objective.title)) {
			const getValue = dict.get(keyResult.objective.title);
			getValue?.push(keyResult);
			dict.set(keyResult.objective.title, getValue || []);
		} else {
			dict.set(keyResult.objective.title, [keyResult]);
		}
	});

	for (const [key, value] of dict) {
		keyResultsGroups.push({
			label: key,
			options: value,
		});
	}

	return keyResultsGroups;
};

export default function SelectKeyResult({ assignedTo, cycles, values, onChange }: SelectKeyResultProps) {
	const [inputValue, setInputValue] = useState<string>("");

	const loadOptions = useCallback(
		async (titleIncludingObjectives: string) => {
			const response = await fetchQuery<SelectKeyResultDropdownQuery>(environment, keyResultsQuery, {
				titleIncludingObjectives,
				assignedTo,
				cycles,
			}).toPromise();
			if (response?.keyResults?.edges) {
				return formatOkrsOptions(response.keyResults.edges);
			}
			return [];
		},
		[assignedTo, cycles],
	);

	const getOptionValue = useCallback((a: KeyResultType) => a.id || "", []);

	const onValueChange = useCallback(
		(value: OnChangeValue<KeyResultType, true>) => {
			onChange((value || []) as KeyResultType[]);
			setInputValue("");
		},
		[onChange],
	);

	const onInputChange = useCallback(
		(val: string, meta: InputActionMeta) => {
			if (meta.action === "input-change") {
				setInputValue(val);
			}
		},
		[setInputValue],
	);

	const MultiValueLabel = (props: MultiValueGenericProps<KeyResultType, true, GroupedKeyResultsOption>) => {
		const keyResult = props.data as KeyResultType;

		if (keyResult.title.trim()) {
			return <div className={styles.multiValueLabel}>{keyResult.title.trim()}</div>;
		}

		return (
			<div className={styles.multiValueLabel}>
				<i>Untitled key result</i>
			</div>
		);
	};

	const KeyResultOption = (props: OptionProps<KeyResultType, true, GroupedKeyResultsOption>) => {
		const keyResult = props.data as KeyResultType;

		return (
			<div {...props.innerProps} className={styles.keyResultOption}>
				<span className={styles.optionIcon}>∟</span>
				{keyResult.title.trim() ? (
					<span className={styles.keyResultOptionTitle}>{keyResult.title.trim()}</span>
				) : (
					<span className={styles.keyResultOptionTitle}>
						<i>Untitled key result</i>
					</span>
				)}
			</div>
		);
	};

	const customStyles: StylesConfig<KeyResultType, true, GroupedKeyResultsOption> = {
		control: _base => ({
			display: "flex",
			border: "1px solid var(--dividerColor)",
			borderTopLeftRadius: "4px",
			borderTopRightRadius: "4px",
			background: "var(--secondLayerColor)",
			"&:hover > div:last-child": {
				background: "var(--veryMutedGrey)",
				color: "initial",
				borderTopRightRadius: "4px",
				borderBottomRightRadius: "4px",
			},
			"&> div:last-child": {
				background: "initial",
				color: "initial",
				transform: "rotate(0)",
			},
		}),
		valueContainer: () => ({
			flex: "1",
			padding: "5px 8px",
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			overflow: "hidden",
			cursor: "pointer",
		}),
		multiValueRemove: base => ({
			...base,
			backgroundColor: "var(--royal100)",
			color: "var(--royal600)",
		}),
		menu: base => ({
			...base,
			position: "relative",
			background: "var(--secondLayerColor)",
			marginTop: "0px",
			borderTopLeftRadius: "0px",
			borderTopRightRadius: "0px",
			boxShadow: "none",
			border: "1px solid var(--dividerColor)",
			borderTop: "0px",
			zIndex: 0,
		}),
		menuList: base => ({
			...base,
			height: "280px",
			padding: "0px 8px",
		}),
		placeholder: () => ({
			color: "var(--placeholderTextColor)",
			fontSize: "14px",
			paddingLeft: "8px",
			position: "absolute",
		}),
		group: () => ({
			fontSize: "14px",
		}),
		groupHeading: () => ({
			color: "var(--placeholderTextColor)",
			padding: "0px 6px",
			margin: "8px 0px",
		}),
		input: base => ({
			...base,
			color: "var(--primaryTextColor)",
		}),
	};

	return (
		<AsyncSelect<KeyResultType, true, GroupedKeyResultsOption>
			key={JSON.stringify(assignedTo) + JSON.stringify(cycles)}
			defaultOptions
			isMulti
			autoFocus
			placeholder="Search by objective or key result"
			value={values}
			onChange={onValueChange}
			inputValue={inputValue}
			onInputChange={onInputChange}
			loadOptions={loadOptions}
			getOptionValue={getOptionValue}
			components={{
				MultiValueLabel: MultiValueLabel,
				Option: KeyResultOption,
				DropdownIndicator: null,
				IndicatorSeparator: null,
			}}
			menuPlacement={"bottom"}
			menuIsOpen={true}
			styles={customStyles}
			formatGroupLabel={formatGroupLabel}
			className={styles.keyResultList}
		/>
	);
}
